import React, { useState, useEffect } from 'react';
import { CrowdVoltListing } from '../types';
import { Button, Flex, Input, Text, Card } from './base-components';
import { useUpdateCrowdVoltListing } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import { trackEvent } from '../services/analytics';

interface Props {
  cvListing: CrowdVoltListing | null;
  ftPricePerTicket?: string | null;
  open: boolean;
  onClose: () => void;
}

export const EditCVListingModal: React.FC<Props> = ({ cvListing, ftPricePerTicket, open, onClose }) => {
  const { showSuccess, showError } = useToast();
  const updateMutation = useUpdateCrowdVoltListing();

  const [price, setPrice] = useState('');
  const [qty, setQty] = useState('');

  useEffect(() => {
    if (cvListing) {
      setPrice(String(cvListing.price));
      setQty(String(cvListing.qty));
    }
  }, [cvListing]);

  useEffect(() => {
    if (!open) {
      setPrice('');
      setQty('');
    }
  }, [open]);

  if (!open || !cvListing) return null;

  const handleSave = async () => {
    if (!price && !qty) {
      showError('Enter a price or qty to update');
      return;
    }
    const parsedPrice = price ? parseFloat(price) : undefined;
    const parsedQty = qty ? parseInt(qty, 10) : undefined;
    if (parsedPrice !== undefined && isNaN(parsedPrice)) {
      showError('Invalid price');
      return;
    }
    if (parsedQty !== undefined && isNaN(parsedQty)) {
      showError('Invalid quantity');
      return;
    }
    try {
      await updateMutation.mutateAsync({
        ask_uqid: cvListing.ask_uqid,
        data: { price: parsedPrice, qty: parsedQty },
      });
      showSuccess('CrowdVolt listing updated');
      trackEvent('cv_listing_updated', { askUqid: cvListing.ask_uqid, price: price || '' });
      onClose();
    } catch (err: any) {
      showError(err?.message || 'Failed to update CrowdVolt listing');
    }
  };

  return (
    <Flex
      align="center"
      justify="center"
      fixed
      inset
      bg="black/80"
      backdrop
      zIndex={1000}
      animate="fadeIn"
      onClick={onClose}
    >
      <Card
        fullWidth
        maxWidth="sm"
        shadow
        animate="slideUp"
        bg="dark"
        padding="lg"
        hover={false}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <Flex align="center" gap={3} mb={3}>
          <div className="w-9 h-9 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
            <span className="text-violet-400 text-sm font-bold">CV</span>
          </div>
          <div>
            <Text variant="heading">Edit CrowdVolt Price</Text>
            <Text variant="muted" size="sm">{cvListing.event_name}</Text>
          </div>
        </Flex>

        {/* CrowdVolt-only note */}
        <div className="mb-4 p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
          <p className="text-sm text-white/60">
            CrowdVolt only
            {ftPricePerTicket && (
              <span className="text-white/40"> · FT listing stays at ${parseFloat(ftPricePerTicket).toFixed(2)}/ticket</span>
            )}
          </p>
        </div>

        <Flex gap={3} mb={4}>
          <Flex direction="col" grow>
            <Input
              type="number"
              label="Price ($)"
              placeholder="0.00"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </Flex>
          <Flex direction="col" grow>
            <Input
              type="number"
              label="Qty"
              placeholder="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </Flex>
        </Flex>

        <Flex justify="end" gap={2}>
          <Button variant="secondary" onClick={onClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </Flex>
      </Card>
    </Flex>
  );
};
